import type {
  AppEventKey,
  AppEventMap,
  EventArgs,
  EventHandler,
  IEventBus,
  ILogger,
  Unsubscribe,
} from "@crialume/sdk";

type AnyHandler = (payload: unknown) => void;

export class EventBus implements IEventBus {
  private readonly handlers = new Map<AppEventKey, Set<AnyHandler>>();

  constructor(private readonly logger?: ILogger) {}

  emit<K extends AppEventKey>(event: K, ...args: EventArgs<K>): void {
    const payload = args[0] as AppEventMap[K];
    this.logger?.debug(`emit ${String(event)}`, payload);

    // Cópia: handlers que se removem durante o emit não afetam a iteração.
    for (const handler of [...(this.handlers.get(event) ?? [])]) {
      try {
        handler(payload);
      } catch (error) {
        // Um listener com erro não pode impedir os demais.
        this.logger?.error(`listener de "${String(event)}" falhou`, error);
      }
    }
  }

  on<K extends AppEventKey>(event: K, handler: EventHandler<K>): Unsubscribe {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler as AnyHandler);
    return () => {
      set.delete(handler as AnyHandler);
    };
  }

  once<K extends AppEventKey>(event: K, handler: EventHandler<K>): Unsubscribe {
    const off = this.on(event, (payload) => {
      off();
      handler(payload);
    });
    return off;
  }

  listenerCount(event: AppEventKey): number {
    return this.handlers.get(event)?.size ?? 0;
  }
}

/**
 * Bus por módulo: compartilha o canal global, mas rastreia as inscrições do
 * módulo para removê-las todas no unload — sem depender de o módulo lembrar
 * de fazer cleanup (vazamento comum no CadFrn).
 */
export function createScopedBus(bus: IEventBus): { bus: IEventBus; dispose: () => void } {
  const subscriptions = new Set<Unsubscribe>();

  const track = (off: Unsubscribe): Unsubscribe => {
    const wrapped = () => {
      off();
      subscriptions.delete(wrapped);
    };
    subscriptions.add(wrapped);
    return wrapped;
  };

  return {
    bus: {
      emit: (event, ...args) => bus.emit(event, ...args),
      on: (event, handler) => track(bus.on(event, handler)),
      once: (event, handler) => track(bus.once(event, handler)),
    },
    dispose: () => {
      for (const off of [...subscriptions]) off();
    },
  };
}
